<template>
  <div class="hyp-calendarevents-calendar">
    <v-sheet height="50">
      <v-toolbar flat height="50" class="hyp-calendar-toolbar">
        <v-btn
          small
          outlined
          class="mr-4"
          color="grey darken-2"
          @click="setToday"
        >
          Today
        </v-btn>
        <v-btn fab text small color="grey darken-2" @click="prev">
          <v-icon small>
            mdi-chevron-left
          </v-icon>
        </v-btn>
        <v-btn fab text small color="grey darken-2" @click="next">
          <v-icon small>
            mdi-chevron-right
          </v-icon>
        </v-btn>
        <v-toolbar-title v-if="$refs.calendar">
          {{ $refs.calendar.title }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-menu bottom right>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              outlined
              color="grey darken-2"
              v-bind="attrs"
              v-on="on"
            >
              <span>{{ typeToLabel[type] }}</span>
              <v-icon right>
                mdi-menu-down
              </v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="type = 'day'">
              <v-list-item-title>Day</v-list-item-title>
            </v-list-item>
            <v-list-item @click="type = 'week'">
              <v-list-item-title>Week</v-list-item-title>
            </v-list-item>
            <v-list-item @click="type = 'month'">
              <v-list-item-title>Month</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>
    </v-sheet>
    <v-sheet height="65vh" elevation="0">
      <v-calendar
        v-if="start && end"
        ref="calendar"
        v-model="calendarSelectedProperties"
        color="primary"
        :events="events"
        :event-color="getEventColor"
        :type="type"
        :start="start"
        :end="end"
        @click:event="showEvent"
        @click:more="viewDay"
        @click:date="viewDay"
        @change="updateRange"
      ></v-calendar>
    </v-sheet>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import lodashGet from "lodash/get";

const EMITTED_ACTIONS = {
  GET_DATA: "get-data",
  SHOW_EVENT: "show-event",
};

export default {
  name: "CalendarEventsCalendar",
  props: {
    items: {
      type: Array,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    loading: {
      type: Boolean,
    },
  },
  data() {
    return {
      severityColors: {
        low: "green",
        medium: "orange",
        high: "red",
      },
      calendarSelectedProperties: "",
      type: "month",
      typeToLabel: {
        month: "Month",
        week: "Week",
        day: "Day",
      },
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      start: null,
      end: null,
    };
  },
  mounted() {
    this.start = dayjs(this.params.start).format("YYYY-MM-DD");
    this.end = dayjs(this.params.end).format("YYYY-MM-DD");
  },
  computed: {
    events() {
      if (this.items?.length > 0) {
        const events = [];
        this.items.forEach((item) => {
          const event = item.properties?.formatted;
          let color = "gray";
          if (event.severity) {
            color = this.severityColors[event.severity.toLowerCase()];
          }
          event.name = event?.title;
          event.start = event?.eventDaterange?.gte;
          event.end = event?.eventDaterange?.lte;
          event.color = color;
          event.timed = false;
          event.impactCategoriesDisplay = event.impactCategories?.join(", ");
          event.categoryAndSubCategory = event.subCategory
            ? event.category + " | " + event.subCategory
            : event.category;

          let iconSrc = "default";
          if (event.category) {
            iconSrc = event.category
              .toLowerCase()
              .replace(" ", "_")
              .replace("(", "")
              .replace(")", "");
          }
          try {
            event.icon = require(`@/assets/svgs/calendarEvents/${iconSrc}.svg`);
          } catch (e) {
            event.icon = require(`@/assets/svgs/calendarEvents/default.svg`);
          }
          event.tagsDisplay = event.tags?.join(", ") || null;
          event.perpetratorDisplay = event.perpetrator?.join(", ") || null;
          events.push(event);
        });
        return events;
      } else {
        return [];
      }
    },
  },
  methods: {
    viewDay({ date }) {
      this.calendarSelectedProperties = date;
      this.type = "day";
    },
    setToday() {
      this.calendarSelectedProperties = new dayjs().format("YYYY-MM-DD");
      this.type = "day";
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    getEventColor(event) {
      // Get color based on event severity
      return event.color;
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        );
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    updateRange({ start, end }) {
      if (
        (dayjs(start.date).isBefore(dayjs("2024-01-01")) &&
          this.type != "week") ||
        (dayjs(start.date).isBefore(dayjs("2023-12-31")) && this.type == "week")
      ) {
        // We don't have data past 2024-01-01
        this.$refs.calendar.next();
        return;
      }
      if (
        dayjs(this.params.start).format("YYYY-MM-DD") != start.date ||
        dayjs(this.params.end).format("YYYY-MM-DD") != end.date
      ) {
        this.params.start = start.date;
        this.params.end = end.date;
        this.getEvents();
      }
    },
    getEvents() {
      this.$emit(EMITTED_ACTIONS.GET_DATA, this.params);
    },
  },
  watch: {
    selectedOpen: {
      async handler() {
        if (this.selectedOpen) {
          this.$emit(EMITTED_ACTIONS.SHOW_EVENT, this.selectedEvent);
        }
      },
    },
  },
};
</script>

<style scoped>
>>> .hyp-calendar-toolbar .v-toolbar__content {
  padding: 0px !important;
}
>>> .theme--light.v-calendar-weekly .v-calendar-weekly__day {
  margin: 2px !important;
  border-radius: 5px !important;
  border: #e0e0e0 1px solid !important;
}
>>> .theme--light.v-calendar-weekly .v-calendar-weekly__day.v-outside {
  border: #f7f7f7 1px solid !important;
}
>>> .theme--light.v-calendar-weekly {
  border: 0 !important;
}
>>> .v-calendar .v-calendar-weekly__head-weekday,
>>> .theme--light.v-calendar-weekly .v-calendar-weekly__head-weekday.v-outside {
  border: 0px !important;
  background-color: unset;
  color: #000000;
}
>>> .v-calendar .v-event {
  border-radius: 10px !important;
}
>>> .v-calendar .v-event.orange {
  background-color: #ff9800cc !important;
  border-color: #ff9800 !important;
}
>>> .v-calendar .v-event.red {
  background-color: #f44336cc !important;
  border-color: #f44336 !important;
}
>>> .v-calendar .v-event.green {
  background-color: #4caf50cc !important;
  border-color: #4caf50 !important;
}
>>> .v-calendar-weekly__day-label .v-btn--fab.v-size--small {
  height: 22px;
  width: 22px;
}
</style>
